<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Popups -->
    <NotificationPopup
      ref="notification"
      :type="notificationType"
      :title="notificationTitle"
      :text="notificationText"
    />
    <ConfirmSiteChanges
      v-model="showConfirmSave"
      @confirmed="updatePartner()"
    />
    <!-- Popups -->

    <!-- Loading -->
    <div
      v-if="loading"
      class="w-full md:h-[70vh] h-[50vh] flex items-center justify-center"
    >
      <div class="w-32 h-32 -mt-10">
        <SpinnerBounce />
      </div>
    </div>

    <!-- Ready -->
    <div v-else class="flex flex-col">
      <!-- Header -->
      <div>
        <div
          class="border-b border-gray-200 pb-5 mb-5 flex items-center justify-between flex-wrap gap-5 dark:border-neutral-700"
        >
          <!-- Tab Header -->
          <h3 class="primary_header">Site Settings</h3>
          <div class="flex gap-5 items-center flex-wrap">
            <div
              class="button_secondary w-[210px]"
              @click="ref_copy_site_signup_link.copy()"
            >
              Copy Site Link (Sign Up)
              <AppCopy ref="ref_copy_site_signup_link" :text="signupLink" />
            </div>
            <div
              class="button_secondary w-[145px]"
              @click="ref_copy_site_link.copy()"
            >
              Copy Site Link
              <AppCopy ref="ref_copy_site_link" :text="loginLink" />
            </div>
          </div>
        </div>
      </div>

      <!-- Forms -->
      <form
        ref="safe_partner_settings_form"
        class="flex flex-wrap gap-10 sm:p-5"
        @submit.prevent="validateSchema"
      >
        <div class="w-full flex flex-wrap gap-10">
          <!-- Title -->
          <div class="w-72">
            <AppInput
              id="title"
              ref="safe_title"
              v-model="partner.title"
              tooltip="This is the title of your site, it will be displayed in the header of your site and in the browser tab"
              label="Title"
              placeholder="Your Company"
            />
          </div>

          <!-- Logo Text -->
          <div class="w-72">
            <AppInput
              id="logo_text"
              ref="safe_logo_text"
              v-model="partner.logo_text"
              tooltip="This is the text that will displayed to the right of the logo, leave this blank if you do not want any text to be displayed next to the logo"
              label="Logo Text"
              placeholder="Your Company"
              :optional="true"
            />
          </div>

          <!-- Landing Page -->
          <div class="w-52">
            <AppSelect
              id="redirect_url"
              ref="safe_select_redirect_url"
              v-model="selected_redirect_url"
              type="redirect_urls"
              label="Investor Landing Page"
              :items="redirect_url_labels"
            />
          </div>

          <!-- Customer Support Email -->
          <div class="w-72">
            <AppInput
              id="support_email"
              ref="safe_support_email"
              v-model="partner.support_email"
              :tooltip="'This email address will be used for all support related communication with your investors'"
              label="Support Email"
              placeholder="support@your-domain.com"
            />
          </div>

          <!-- Bcc Email -->
          <div class="w-72">
            <AppInput
              id="bcc_email"
              ref="safe_bcc_email"
              v-model="partner.bcc_email"
              tooltip="This email address will be Bcc'd on all outgoing communication to your investors. This is used for record keeping purposes."
              label="Bcc Email"
              placeholder="bcc@your-domain.com"
              :optional="true"
            />
          </div>
        </div>

        <!-- Color Fields -->
        <div class="flex flex-wrap gap-10 w-full">
          <!-- Logo Text Color -->
          <div class="w-40">
            <SafeColorInput
              id="title_color"
              ref="safe_title_color"
              v-model="partner.title_color"
              label="Logo Text Color"
              tooltip="This is the color used only for the logo text on you site"
            />
          </div>
          <!-- Dark Mode Logo Text -->
          <div class="w-52">
            <SafeColorInput
              id="title_color_dark"
              ref="safe_title_color_dark"
              v-model="partner.title_color_dark"
              label="Dark Mode Logo Text Color"
              tooltip="This is the color used only for the logo text on you site, when a user is in dark mode"
            />
          </div>
          <!-- Primary Color -->
          <div class="w-40">
            <SafeColorInput
              id="primary_color"
              ref="safe_primary_color"
              v-model="partner.primary_color"
              label="Primary Color"
              tooltip="The primary color is used for primary buttons, active tabs, etc."
            />
          </div>
          <!-- Light Primary Color -->
          <div class="w-40">
            <SafeColorInput
              id="primary_color_light"
              ref="safe_primary_color_light"
              v-model="partner.primary_color_light"
              label="Light Primary Color"
              tooltip="The light primary color is used for hover states, We recommend it match you primary color but just a few shades lighter."
            />
          </div>
          <!-- Dark Primary Color -->
          <div class="w-40">
            <SafeColorInput
              id="primary_color_dark"
              ref="safe_primary_color_dark"
              v-model="partner.primary_color_dark"
              label="Dark Primary Color"
              tooltip="The dark primary color is used for hover states in dark mode, We recommend it match you primary color but just a few shades darker."
            />
          </div>
        </div>

        <!-- Image Fields -->
        <div class="flex flex-wrap gap-10 w-full">
          <!-- Logo Image -->
          <div>
            <label
              for="circle-image-upload"
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Logo Image
              <Tooltip
                content="Accepted file types png & jpeg. Files less then 10MB recommended. We recommend using a square image for best results."
              >
                <QuestionMarkCircleIcon
                  class="size-4 text-gray-600 cursor-pointer dark:text-neutral-400 hover:text-operacolorlight"
                />
              </Tooltip>
            </label>
            <div class="flex items-center mt-2 gap-x-3">
              <PhotoIcon
                v-if="!logoUrl"
                class="w-12 h-12 text-gray-300"
                aria-hidden="true"
              />
              <img
                v-else
                :src="logoUrl"
                alt="Logo Image"
                class="w-12 h-12 object-cover"
              />
              <label
                for="circle-image-upload"
                type="button"
                class="button_secondary"
                >Change</label
              >
              <input
                id="circle-image-upload"
                name="circle-image-upload"
                type="file"
                class="sr-only"
                accept="image/*"
                @change="logoImageChanged"
              />
            </div>
          </div>

          <!-- Background Image -->
          <div>
            <label
              for="background-image-upload"
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Background Image
              <Tooltip
                content="Accepted file types png & jpeg. Files less then 10MB recommended. We recommend using a landscape image for best results."
              >
                <QuestionMarkCircleIcon
                  class="size-4 text-gray-600 cursor-pointer dark:text-neutral-400 hover:text-operacolorlight"
                />
              </Tooltip>
            </label>
            <div class="flex items-center mt-2 gap-x-3">
              <PhotoIcon
                v-if="!backgroundUrl"
                class="w-12 h-12 text-gray-300"
                aria-hidden="true"
              />
              <img
                v-else
                :src="backgroundUrl"
                alt="Logo Image"
                class="w-24 h-12 rounded-lg object-fit"
              />
              <label
                for="background-image-upload"
                type="button"
                class="button_secondary"
                >Change</label
              >
              <input
                id="background-image-upload"
                name="background-image-upload"
                type="file"
                class="sr-only"
                accept="image/*"
                @change="backgroundImageChanged"
              />
            </div>
          </div>
        </div>

        <!-- Welcome Email Customization -->
        <div
          class="flex flex-col gap-5 p-5 shadow bg-white rounded-lg dark:bg-neutral-900 max-w-3xl h-fit"
        >
          <!-- Collect Income Data -->
          <SafeSwitch
            ref="safe_switch_disable_signup"
            v-model="partner.disable_signup"
            label="Disable Sign Up"
            color="red"
            description="If this is turned on no new users can sign up and create accounts in your Opera environment, Use with caution."
          />

          <!-- Welcome Email -->
          <SafeSwitch
            ref="safe_switch_send_welcome_email"
            v-model="partner.send_welcome_email"
            label="Send Welcome Email"
            description="Send a custom welcome email when new users sign in for the first time? (Required for RegCF)"
          />

          <!-- Welcome Email HTML/Text -->
          <div v-if="partner.send_welcome_email" class="w-full">
            <label
              for="additional-content"
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Welcome Email Content
            </label>
            <div class="text-sm text-gray-500 dark:text-neutral-400 pb-1">
              This should be in html format or plain text. We recommend using
              <a
                href="https://tabular.email/"
                target="_blank"
                class="text-blue-600 underline cursor-pointer"
                >Tabular</a
              >
              to create custom emails. If you choose to use Tabular, when your
              email is looking the way you like in your tabular editor click the
              'Use Email' button in the top right corner and choose HTML as your
              export option then copy the 'HTML' code generated and paste it
              here. Don't forget to click 'Save & Publish' at the bottom of the
              page to save your changes!
            </div>
            <SafeTextarea
              id="welcome_email_content"
              ref="safe_welcome_email_content"
              v-model="partner.welcome_email_content"
              :rows="6"
              placeholder="Hello From Opera! Thanks for creating an account!"
            />
          </div>

          <!-- Welcome Email Preview -->
          <div v-if="partner.send_welcome_email">
            <div
              class="block text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Welcome Email Preview
            </div>
            <div class="text-sm text-gray-500 dark:text-neutral-400 mb-3">
              If you are getting an error or do not like how your email is
              looking you can.
              <a
                class="text-orange-500 underline cursor-pointer font-semibold"
                @click="setWelcomeEmailToDefault"
                >Use Default Email</a
              >
            </div>
            <div
              class="rounded-lg shadow-opera w-full h-[600px] overflow-hidden"
            >
              <iframe
                class="w-full h-full"
                :srcdoc="partner.welcome_email_content"
              ></iframe>
            </div>
          </div>

          <!-- Include Additional Links in Sign in Email -->
          <SafeSwitch
            ref="safe_switch_use_additional_signin_content"
            v-model="partner.use_additional_signin_content"
            label="Add link (Recommended for RegCF Offerings as a link to education materials)"
            description="When this is turned on the link you add in the 'Add link to Sign in Email' text area will be available in the footer of all login and signup emails sent to your users."
          />

          <!-- Additional Sign in Link -->
          <div v-if="partner.use_additional_signin_content" class="w-full">
            <AppInput
              id="additional_signin_content_text"
              ref="safe_additional_signin_content_text"
              v-model="partner.additional_signin_content_text"
              label="Add link to Sign in Email"
              placeholder="ex. https://operaalts.com/education"
            />
          </div>
        </div>

        <!-- Thank You Email Customization -->
        <div
          class="flex flex-col gap-5 p-5 shadow bg-white rounded-lg dark:bg-neutral-900 max-w-3xl h-fit"
        >
          <!-- Use Custom Thank you Email -->
          <SafeSwitch
            ref="safe_switch_use_custom_thank_you_email"
            v-model="partner.use_custom_thank_you_email"
            label="Use Custom Thank You Email"
            description="This is also known as the 'Investment Confirmation' email. If you would like to use the default thank you email, simply turn this off."
          />

          <!-- Welcome Email HTML/Text -->
          <div v-if="partner.use_custom_thank_you_email" class="w-full">
            <label
              for="additional-content"
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Thank You Email Content
            </label>
            <div class="text-sm text-gray-500 dark:text-neutral-400">
              This should be in html format or plain text. We recommend using
              <a
                href="https://tabular.email/"
                target="_blank"
                class="text-blue-600 underline cursor-pointer"
                >Tabular</a
              >
              to create custom emails. If you choose to use Tabular, when your
              email is looking the way you like in your tabular editor click the
              'Use Email' button in the top right corner and choose HTML as your
              export option then copy the 'HTML' code generated and paste it
              here. Don't forget to click 'Save & Publish' at the bottom of the
              page to save your changes!
            </div>

            <div
              class="text-sm text-gray-700 dark:text-neutral-300 mt-3 mb-4 py-2 px-3 bg-gray-100 dark:bg-neutral-800 rounded-md"
            >
              <div>
                Often you may want to include information that is unique to each
                investor and investment in each Thank You email. To do this in a
                custom email you can use the follow special codes:
              </div>
              <div class="text-base p-2 font-semibold">
                {{ explain_offering_name }}
              </div>
              <div class="text-base p-2 font-semibold">
                {{ explain_investment_amount }}
              </div>
              <div class="text-base p-2 font-semibold">
                {{ explain_num_share }}
              </div>
              <div class="text-base p-2 font-semibold">
                {{ explain_payment_method }}
              </div>
              <div class="text-base p-2 font-semibold">
                {{ explain_support_email }}
              </div>
              <div class="mt-1">
                Here is an example of how you might use these codes in your
                custom email:
              </div>
              <div class="p-2 font-medium">
                {{ example_email }}
              </div>
              <div class="mt-1">
                This is how the email would look to the user when they receive
                it:
              </div>
              <div class="p-2 font-medium">
                Thank you for investing in Opera's Offering. Your investment of
                $1,500 via ACH is appreciated. If you have any questions please
                contact us at support@operaalts.com.
              </div>
            </div>

            <!-- Thank You Email Content -->
            <SafeTextarea
              id="thank_you_email_content"
              ref="safe_thank_you_email_content"
              v-model="partner.thank_you_email_content"
              :rows="6"
              placeholder="Hello From Opera! Thanks for creating an account!"
            />

            <!-- Thank You Email Preview -->
            <div v-if="partner.use_custom_thank_you_email" class="mt-5">
              <div
                class="block text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300 mb-2"
              >
                Thank You Email Preview
              </div>
              <div class="text-sm text-gray-500 dark:text-neutral-400 mb-3">
                If you are getting an error or do not like how your email is
                looking you can.
                <a
                  class="text-orange-500 underline cursor-pointer font-semibold"
                  @click="setThankYouEmailToDefault"
                  >Use Default Email</a
                >
              </div>
              <div
                class="rounded-lg shadow-opera w-full h-[600px] overflow-hidden"
              >
                <iframe
                  class="w-full h-full"
                  :srcdoc="partner.thank_you_email_content"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        <!-- Boolean Fields -->
        <div
          class="flex flex-col gap-5 p-5 shadow bg-white h-fit rounded-lg dark:bg-neutral-900 max-w-3xl"
        >
          <!-- Collect Income Data -->
          <SafeSwitch
            ref="safe_switch_collect_income_data"
            v-model="partner.collect_income_data"
            label="Collect Income Data"
            description="Collect user's income data when signing up?"
          />
          <!-- Collect Employer Data -->
          <SafeSwitch
            ref="safe_switch_collect_employer_data"
            v-model="partner.collect_employer_data"
            label="Collect Employment Data"
            description="Collect user's employment data when signing up?"
          />
          <!-- Enable Notifications for Accreditation Status -->
           <SafeSwitch
            ref="safe_switch_disable_accreditation"
            v-model="partner.disable_accreditation"
            label="Disable Accreditation"
            description="If this is disabled, Opera IQ will only provide KYC and AML services. Accreditation questions will not be asked. You should only disable this if you know what you are doing."
            color="red"
          />
          <SafeSwitch
            ref="safe-switch_send_accreditation_notifications"
            v-model="partner.send_accreditation_notifications"
            label="Send Accreditation Status Notifications"
            description="If this is disabled, users will not receive emails when their accreditation status is updated."
          />

          <!-- What Investor Types are Allowed -->
          <!-- TODO -->
          <div class="mt-2 flex flex-col gap-5 w-full">
            <fieldset>
              <legend
                class="text-base font-semibold leading-6 text-gray-900 dark:text-white flex gap-1 items-center"
              >
                Allowed Account Types
                <Tooltip
                  content="Select all the types of investors you would will allow. At least one is required."
                >
                  <QuestionMarkCircleIcon
                    class="size-5 text-gray-600 cursor-pointer dark:text-neutral-400"
                  />
                </Tooltip>
              </legend>
              <div
                class="mt-4 divide-y divide-gray-200 border-t border-gray-200 dark:border-neutral-700 dark:divide-neutral-700"
              >
                <div class="relative flex items-start py-4">
                  <AppCheckbox
                    v-model="allow_individual_account"
                    label="Individual Accounts"
                    class="pl-1"
                  />
                </div>
                <div class="relative flex items-start py-4">
                  <AppCheckbox
                    v-model="allow_joint_account"
                    label="Joint Accounts"
                    class="pl-1"
                  />
                </div>
                <div class="relative flex items-start py-4">
                  <AppCheckbox
                    v-model="allow_entity_account"
                    label="Entity Accounts"
                    class="pl-1"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </div>

        <!-- Copiable Fields -->
        <div class="flex flex-wrap gap-10 w-full">
          <!-- Meta Name -->
          <div class="w-72">
            <div
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Meta Tag
            </div>
            <div
              class="py-1.5 px-3 mt-2 flex items-center justify-between gap-2 border border-gray-300 dark:border-neutral-600 rounded-md border-dashed"
            >
              <div
                class="text-base italic font-medium text-gray-600 dark:text-neutral-300"
              >
                {{ partner.meta_name }}
              </div>
              <AppCopy :text="partner.meta_name" />
            </div>
          </div>
          <!-- Created At -->
          <div class="w-72">
            <div
              class="flex items-center gap-2 text-sm font-medium leading-6 text-gray-900 dark:text-neutral-300"
            >
              Created
            </div>
            <div
              class="py-2 px-3 mt-2 flex items-center justify-between gap-2 border border-gray-300 dark:border-neutral-600 rounded-md border-dashed"
            >
              <div class="text-sm text-gray-700 dark:text-neutral-300">
                {{ formatDate(partner.created_at) }}
              </div>
              <AppCopy :text="formatDate(partner.created_at)" />
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex gap-4 w-full">
          <ButtonPrimary type="submit" text="Save & Publish" size="lg" />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, onMounted, watch, computed } from "vue";

// Stores
import { usePartnerStore } from "@/stores/partner";
const partnerStore = usePartnerStore();

// Schemas
import { PartnerSettingsSchema } from "@/schemas/PartnerSettingsSchema.ts";

// Components
import SpinnerBounce from "@/components/loading/SpinnerBounce.vue";
import NotificationPopup from "@/components/applicationui/AppNotification.vue";
import ConfirmSiteChanges from "@/components/partners/ConfirmSiteChanges.vue";
import AppInput from "@/components/applicationinputs/AppInput.vue";
import SafeColorInput from "@/components/applicationinputs/AppColorInput.vue";
import SafeSwitch from "@/components/applicationinputs/safeSwitch.vue";
import AppSelect from "@/components/applicationinputs/AppSelect.vue";
import SafeTextarea from "@/components/applicationinputs/safeTextarea.vue";
import AppCheckbox from "@/components/applicationinputs/AppCheckbox.vue";
import ButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";
import AppCopy from "@/components/applicationui/AppCopy.vue";
import Tooltip from "@/components/applicationui/AppTooltip.vue";

// Libraries
import { formatDate } from "@/helper/helper";
import { PhotoIcon, QuestionMarkCircleIcon } from "@heroicons/vue/24/outline";

// HTML Only Refs START
const ref_copy_site_link = ref(null);
const ref_copy_site_signup_link = ref(null);
// HTML Only Refs END

// User Error Handling START
const safe_partner_settings_form = ref(null);
const safe_title = ref(null);
const safe_logo_text = ref(null);
const safe_select_redirect_url = ref(null);
const safe_support_email = ref(null);
const safe_bcc_email = ref(null);
const safe_title_color = ref(null);
const safe_title_color_dark = ref(null);
const safe_primary_color = ref(null);
const safe_primary_color_light = ref(null);
const safe_primary_color_dark = ref(null);
const safe_switch_disable_signup = ref(null);
const safe_switch_send_welcome_email = ref(null);
const safe_welcome_email_content = ref(null);
const safe_use_additional_signin_content = ref(null);
const safe_additional_signin_content = ref(null);
const safe_switch_use_custom_thank_you_email = ref(null);
const safe_thank_you_email_content = ref(null);
const safe_switch_collect_income_data = ref(null);
const safe_switch_collect_employer_data = ref(null);
const safe_switch_disable_accreditation = ref(null);
const safe_send_accreditation_notifications = ref(null);
const safe_allowed_account_types = ref(null);
const refs = {
  safe_logo_text,
  safe_select_redirect_url,
  safe_support_email,
  safe_bcc_email,
  safe_title_color,
  safe_title_color_dark,
  safe_primary_color,
  safe_primary_color_light,
  safe_primary_color_dark,
  safe_switch_disable_signup,
  safe_switch_send_welcome_email,
  safe_welcome_email_content,
  safe_use_additional_signin_content,
  safe_additional_signin_content,
  safe_switch_use_custom_thank_you_email,
  safe_thank_you_email_content,
  safe_switch_collect_income_data,
  safe_switch_collect_employer_data,
  safe_send_accreditation_notifications,
  safe_switch_disable_accreditation,
  safe_allowed_account_types,
};
const getRef = (name) => {
  return refs[name];
};
const showInvalidFields = (errors) => {
  let last_ref = null;
  errors.forEach((error) => {
    const invalid_input_ref = getRef(`safe_${error.path[0]}`);
    const invalid_switch_ref = getRef(`safe_switch_${error.path[0]}`);
    const invalid_select_ref = getRef(`safe_select_${error.path[0]}`);
    if (invalid_input_ref?.value) {
      invalid_input_ref.value?.setErrorMessage(error.message);
      last_ref = invalid_input_ref.value;
    } else if (invalid_switch_ref?.value) {
      invalid_switch_ref.value.showError(error.message);
      notify("failure", "Sorry", error.message);
      last_ref = invalid_switch_ref.value;
    } else if (invalid_select_ref?.value) {
      invalid_select_ref.value.showError(error.message);
      notify("failure", "Sorry", error.message);
      last_ref = invalid_select_ref.value;
    } else notify("failure", "Sorry", error.message);
  });
  // Scroll to the last invalid input
  if (last_ref)
    last_ref.$el.scrollIntoView({ behavior: "smooth", block: "center" });
  else window.scrollTo({ top: 0, behavior: "smooth" });

  // Report validity
  // safe_partner_settings_form.value.reportValidity()
  return;
};

const validateSchema = () => {
  // Strucutre the data
  partner.value.redirect_url = selected_redirect_url.value.url;

  // Validate the schema
  const result = PartnerSettingsSchema.safeParse(partner.value);

  // If there was an error handle it!
  if (!result.success) {
    showInvalidFields(result.error.errors);
    return;
  }

  // If there are no errors, submit the form
  showConfirmSave.value = true;
};
// User Error Handling END

// Update Partner START
async function updatePartner() {
  updating.value = true;
  // Update Partner
  setAllowedAccountTypes();
  const successful = await partnerStore.updatePartner(partner.value);
  if (!successful)
    notify(
      "failure",
      "Sorry",
      "We were unable to update the partner settings, please try again later. If the issue persits, please contact tech support",
    );
  notify(
    "success",
    "Success",
    "Your site settings have been updated successfully",
  );
  // Prepare an array of promises based on conditions
  const promises = [];
  // Push the image uploads if they are needed to the promise array
  if (logoBlob.value)
    promises.push(
      partnerStore.uploadPartnerImages(
        partner.value.meta_name,
        logoBlob.value,
        "logo",
      ),
    );
  if (backgroundBlob.value)
    promises.push(
      partnerStore.uploadPartnerImages(
        partner.value.meta_name,
        backgroundBlob.value,
        "background",
      ),
    );
  // Upload everything in parallel
  await Promise.all(promises);
  // All uploads completed
  updating.value = false;
  showConfirmSave.value = false;
}
// Update Partner END

// Allowed Account Types START
const allow_individual_account = ref(false);
const allow_joint_account = ref(false);
const allow_entity_account = ref(false);
const setAllowedAccountTypes = () => {
  if (allow_individual_account.value) {
    if (!partner.value.allowed_account_types.includes("individual"))
      partner.value.allowed_account_type.push("individual");
  } else {
    const index = partner.value.allowed_account_types.indexOf("individual");
    if (index > -1) partner.value.allowed_account_types.splice(index, 1);
  }
  if (allow_joint_account.value) {
    if (!partner.value.allowed_account_types.includes("joint"))
      partner.value.allowed_account_types.push("joint");
  } else {
    const index = partner.value.allowed_account_types.indexOf("joint");
    if (index > -1) partner.value.allowed_account_types.splice(index, 1);
  }
  if (allow_entity_account.value) {
    if (!partner.value.allowed_account_types.includes("entity"))
      partner.value.allowed_account_types.push("entity");
  } else {
    const index = partner.value.allowed_account_types.indexOf("entity");
    if (index > -1) partner.value.allowed_account_types.splice(index, 1);
  }
};
watch(
  [allow_individual_account, allow_joint_account, allow_entity_account],
  (newValues) => {
    if (!newValues.includes(true)) {
      setTimeout(() => {
        allow_individual_account.value = true;
      }, 500);
    }
  },
);
// Allowed Account Types END

// Redirects START
const selected_redirect_url = ref(null);
const setSelectedRedirectUrl = (url) => {
  selected_redirect_url.value = redirect_map.value.find(
    (item) => item.url === url,
  );
};
const redirect_map = ref([
  { id: 1, label: "Investing", url: "/dashboard/investing" },
  { id: 2, label: "Holdings", url: "/dashboard/holdings" },
  { id: 3, label: "Payment Methods", url: "/dashboard/payment-methods" },
  { id: 4, label: "Documents", url: "/dashboard/documents" },
  { id: 5, label: "Education", url: "/dashboard/education" },
  { id: 6, label: "Accounts", url: "/dashboard/accounts" },
  { id: 7, label: "Settings", url: "/dashboard/settings" },
]);
const redirect_url_labels = computed(() => {
  let labels = partnerStore.partner.partners_protected.investor_tabs.map(
    (id) => {
      return redirect_map.value.find((item) => item.id === id);
    },
  );
  if (!labels.some((label) => label.id === 7)) {
    labels.push({ id: 7, label: "Settings", url: "/dashboard/settings" });
  }
  return labels;
});
// Redirects END

// Images START
// logo
const logoUrl = ref(null);
const logoBlob = ref(null);
const logoImageChanged = (event) => {
  if (
    event.target.files[0].type != "image/jpeg" &&
    event.target.files[0].type != "image/png"
  ) {
    logoBlob.value = null;
  } else {
    logoBlob.value = event.target.files[0];
    logoUrl.value = URL.createObjectURL(event.target.files[0]);
  }
};
// background
const backgroundUrl = ref(null);
const backgroundBlob = ref(null);
const backgroundImageChanged = (event) => {
  if (
    event.target.files[0].type != "image/jpeg" &&
    event.target.files[0].type != "image/png"
  ) {
    backgroundBlob.value = null;
  } else {
    backgroundBlob.value = event.target.files[0];
    backgroundUrl.value = URL.createObjectURL(event.target.files[0]);
  }
};
// Images END

// Notifications START
const notification = ref(null);
const notificationType = ref("success");
const notificationTitle = ref("Success");
const notificationText = ref("");
const notify = (type, title, text) => {
  notificationType.value = type;
  notificationTitle.value = title;
  notificationText.value = text;
  notification.value.show();
};
// Notifications END

// Globals
const partner = ref({});
const loginLink = ref("");
const signupLink = ref("");

// Loading
const loading = ref(true);
const updating = ref(false);

// Visibility Toggles
const showConfirmSave = ref(false);

// Mounted START
onMounted(async () => {
  // Set Partner
  partner.value = partnerStore.partner;

  // Set Redirect URL Object
  setSelectedRedirectUrl(partner.value.redirect_url);

  // Set Images
  logoUrl.value = partnerStore.getLogo;
  backgroundUrl.value = partnerStore.getBackground;

  // Set Emails
  setDefaultEmail();
  setDefaultThankYouEmail();

  // Set Login Links
  loginLink.value = `${window.location.origin}/login?partner=${partner.value.meta_name}`;
  signupLink.value = `${window.location.origin}/signup?partner=${partner.value.meta_name}`;

  // Initialize checkboxes
  allow_individual_account.value =
    partnerStore.partner?.allowed_account_types?.includes("individual");
  allow_joint_account.value =
    partnerStore.partner?.allowed_account_types?.includes("joint");
  allow_entity_account.value =
    partnerStore.partner?.allowed_account_types?.includes("entity");
  // Ensure at least one account type is allowed
  if (
    !allow_individual_account.value &&
    !allow_joint_account.value &&
    !allow_entity_account.value
  )
    allow_individual_account.value = true;

  // Ready
  loading.value = false;
});
// Mounted END

// Emails START
// email constants
const explain_offering_name =
  "{{offering-name}} - this will be replaced with the name of the offering";
const explain_investment_amount =
  "{{investment-amount}} - this will be replaced with the investment amount";
const explain_num_share =
  "{{number-shares}} - this will be replaced with the number of shares";
const explain_payment_method =
  "{{payment-method}} - this will be replaced with the payment method";
const explain_support_email =
  "{{support-email}} - this will be replaced with your site's support email address";
const example_email =
  "Thank you for investing in {{offering-name}}. Your investment of {{investment-amount}} via {{payment-method}} is appreciated. If you have any questions please contact us at {{support-email}}.";
async function setThankYouEmailToDefault() {
  partner.value.thank_you_email_content = default_thank_you_email;
}
const default_thank_you_email = ref("");
const setDefaultThankYouEmail = () => {
  default_thank_you_email.value = `
  <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" lang="en">
<head>
<title></title>
<meta charset="UTF-8" />
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<!--[if !mso]>-->
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<!--<![endif]-->
<meta name="x-apple-disable-message-reformatting" content="" />
<meta content="target-densitydpi=device-dpi" name="viewport" />
<meta content="true" name="HandheldFriendly" />
<meta content="width=device-width" name="viewport" />
<meta name="format-detection" content="telephone=no, date=no, address=no, email=no, url=no" />
<style type="text/css">
table {
border-collapse: separate;
table-layout: fixed;
mso-table-lspace: 0pt;
mso-table-rspace: 0pt
}
table td {
border-collapse: collapse
}
.ExternalClass {
width: 100%
}
.ExternalClass,
.ExternalClass p,
.ExternalClass span,
.ExternalClass font,
.ExternalClass td,
.ExternalClass div {
line-height: 100%
}
body, a, li, p, h1, h2, h3 {
-ms-text-size-adjust: 100%;
-webkit-text-size-adjust: 100%;
}
html {
-webkit-text-size-adjust: none !important
}
body, #innerTable {
-webkit-font-smoothing: antialiased;
-moz-osx-font-smoothing: grayscale
}
#innerTable img+div {
display: none;
display: none !important
}
img {
Margin: 0;
padding: 0;
-ms-interpolation-mode: bicubic
}
h1, h2, h3, p, a {
line-height: inherit;
overflow-wrap: normal;
white-space: normal;
word-break: break-word
}
a {
text-decoration: none
}
h1, h2, h3, p {
min-width: 100%!important;
width: 100%!important;
max-width: 100%!important;
display: inline-block!important;
border: 0;
padding: 0;
margin: 0
}
a[x-apple-data-detectors] {
color: inherit !important;
text-decoration: none !important;
font-size: inherit !important;
font-family: inherit !important;
font-weight: inherit !important;
line-height: inherit !important
}
u + #body a {
color: inherit;
text-decoration: none;
font-size: inherit;
font-family: inherit;
font-weight: inherit;
line-height: inherit;
}
a[href^="mailto"],
a[href^="tel"],
a[href^="sms"] {
color: inherit;
text-decoration: none
}
img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:22px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}
</style>
<style type="text/css">
@media (min-width: 481px) {
.hd { display: none!important }
}
</style>
<style type="text/css">
@media (max-width: 480px) {
.hm { display: none!important }
}
</style>
<style type="text/css">
@media (min-width: 481px) {
h1,img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif}img,p{line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2,h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;font-weight:400;font-style:normal;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h2{line-height:30px;font-size:24px}h3{line-height:26px;font-size:20px}.t31{width:600px!important}.t29{width:86.66667%!important}.t27{padding-top:60px!important;padding-bottom:60px!important}.t1{width:40px!important}.t12,.t16,.t24,.t4,.t8{mso-line-height-alt:30px!important;line-height:30px!important}.t9{width:480px!important}.t3{line-height:34px!important;font-size:28px!important}.t11,.t15{line-height:28px!important;font-size:18px!important}
}
</style>
<style type="text/css">@media (min-width: 481px) {[class~="x_t31"]{width:600px!important;} [class~="x_t29"]{width:86.66667%!important;} [class~="x_t27"]{padding-top:60px!important;padding-bottom:60px!important;} [class~="x_t1"]{width:40px!important;} [class~="x_t8"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t9"]{width:480px!important;} [class~="x_t4"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t3"]{line-height:34px!important;font-size:28px!important;} [class~="x_t12"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t11"]{line-height:28px!important;font-size:18px!important;} [class~="x_t16"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t15"]{line-height:28px!important;font-size:18px!important;} [class~="x_t24"]{mso-line-height-alt:30px!important;line-height:30px!important;}}</style>
<style type="text/css" media="screen and (min-width:481px)">.moz-text-html img,.moz-text-html p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}.moz-text-html h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html .t31{width:600px!important}.moz-text-html .t29{width:86.66667%!important}.moz-text-html .t27{padding-top:60px!important;padding-bottom:60px!important}.moz-text-html .t1{width:40px!important}.moz-text-html .t8{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t9{width:480px!important}.moz-text-html .t4{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t3{line-height:34px!important;font-size:28px!important}.moz-text-html .t12{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t11{line-height:28px!important;font-size:18px!important}.moz-text-html .t16{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t15{line-height:28px!important;font-size:18px!important}.moz-text-html .t24{mso-line-height-alt:30px!important;line-height:30px!important}</style>
<!--[if !mso]>-->
<link href="https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;700&amp;display=swap" rel="stylesheet" type="text/css" />
<!--<![endif]-->
<!--[if mso]>
<style type="text/css">
img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}div.t29{width:86.66667% !important}td.t27{padding-top:60px !important;padding-bottom:60px !important}div.t4,div.t8{mso-line-height-alt:30px !important;line-height:30px !important}h1.t3{line-height:34px !important;font-size:28px !important}div.t12{mso-line-height-alt:30px !important;line-height:30px !important}p.t11{line-height:28px !important;font-size:18px !important}div.t16{mso-line-height-alt:30px !important;line-height:30px !important}p.t15{line-height:28px !important;font-size:18px !important}div.t24{mso-line-height-alt:30px !important;line-height:30px !important}
</style>
<![endif]-->
<!--[if mso]>
<xml>
<o:OfficeDocumentSettings>
<o:AllowPNG/>
<o:PixelsPerInch>96</o:PixelsPerInch>
</o:OfficeDocumentSettings>
</xml>
<![endif]-->
</head>
<body id="body" class="t35" style="min-width:100%;Margin:0px;padding:0px;background-color:#ffffff;"><div class="t34" style="background-color:#ffffff;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td class="t33" style="font-size:0;line-height:0;mso-line-height-rule:exactly;background-color:#ffffff;" valign="top" align="center">
<!--[if mso]>
<v:background xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false">
<v:fill color="#ffffff"/>
</v:background>
<![endif]-->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center" id="innerTable"><tr><td align="center">
<table class="t32" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="600" class="t31">
<![endif]-->
<!--[if !mso]>-->
<td class="t31" style="width:480px;">
<!--<![endif]-->
<div class="t30" style="display:inline-table;width:100%;text-align:center;vertical-align:top;">
<!--[if mso]>
<table role="presentation" cellpadding="0" cellspacing="0" align="center" valign="top" width="520"><tr><td width="520" valign="top"><![endif]-->
<div class="t29" style="display:inline-table;text-align:initial;vertical-align:inherit;width:100%;max-width:520px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t28" style="width:100%!important;"><tr>
<td class="t27" style="padding:50px 20px 50px 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%!important;"><tr><td align="center">
<table class="t2" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="40" class="t1">
<![endif]-->
<!--[if !mso]>-->
<td class="t1" style="width:80px!important;">
<!--<![endif]-->
<div style="font-size:0px;"><img class="t0" style="display:block;border:0;height:auto;width:100%;Margin:0;max-width:100%;" width="60" height="60" alt="" src="${logoUrl.value}"/></div></td>
</tr></table>
</td></tr><tr><td><div class="t4" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t6" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="440" class="t5">
<![endif]-->
<!--[if !mso]>-->
<td class="t5" style="width:440px;">
<!--<![endif]-->
<h1 class="t3" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:22px;text-decoration:none;text-transform:none;direction:ltr;color:#000000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px;">Thank You</h1></td>
</tr></table>
</td></tr><tr><td><div class="t8" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t10" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="480" class="t9">
<![endif]-->
<!--[if !mso]>-->
<td class="t9" style="width:440px;">
<!--<![endif]-->
<div style="font-size:0px;"><img class="t7" style="display:block;border:0;height:auto;width:100%;Margin:0;max-width:100%;border-radius: 0.5rem;" width="480" height="264.484375" alt="" src="${backgroundUrl.value}"/></div></td>
</tr></table>
</td></tr><tr><td><div class="t12" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t14" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="390" class="t13">
<![endif]-->
<!--[if !mso]>-->
<td class="t13" style="width:390px;">
<!--<![endif]-->
<p class="t11" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px;">Thank you for investing with ${partner.value.title}. Your investment of {{investment-amount}} in {{offering-name}} via {{payment-method}} is appreciated.</p></td>
</tr></table>
</td></tr><tr><td><div class="t16" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t18" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="390" class="t17">
<![endif]-->
<!--[if !mso]>-->
<td class="t17" style="width:390px;">
<!--<![endif]-->
<p class="t15" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px;">Your investment may take several business days to process. If you have any questions about your investment you can reach us at {{support-email}}</p></td>
</tr></table>
</td></tr><tr><td><div class="t24" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t26" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="387" class="t25">
<![endif]-->
<!--[if !mso]>-->
<td class="t25" style="width:387px;">
<!--<![endif]-->
<div class="t23" style="display:inline-table;width:100%;text-align:left;vertical-align:top;">
<!--[if mso]>
<table role="presentation" cellpadding="0" cellspacing="0" align="left" valign="top" width="130"><tr><td width="130" valign="top"><![endif]-->
<div class="t22" style="display:inline-table;text-align:initial;vertical-align:inherit;width:33.59173%;max-width:130px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t21" style="width:100%!important;"><tr>
<td class="t20" style="overflow:hidden;background-color:${partner.value.primary_color};text-align:center;line-height:46px;mso-line-height-rule:exactly;mso-text-raise:9px;border-radius:8px 8px 8px 8px;"><a class="t19" href="${window.location.origin}/dashboard/education" style="display:block;margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:46px;font-weight:500;font-style:normal;font-size:16px;text-decoration:none;direction:ltr;color:#FFFFFF;text-align:center;mso-line-height-rule:exactly;mso-text-raise:9px;" target="_blank">Read more</a></td>
</tr></table>
</div>
<!--[if mso]>
</td>
</tr></table>
<![endif]-->
</div></td>
</tr></table>
</td></tr></table></td>
</tr></table>
</div>
<!--[if mso]>
</td>
</tr></table>
<![endif]-->
</div></td>
</tr></table>
</td></tr></table></td></tr></table></div></body>
</html>
`;
};
async function setWelcomeEmailToDefault() {
  partner.value.welcome_email_content = default_welcome_email;
}
const default_welcome_email = ref("");
const setDefaultEmail = () => {
  default_welcome_email.value = `
  <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" lang="en">
<head>
<title></title>
<meta charset="UTF-8" />
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<!--[if !mso]>-->
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<!--<![endif]-->
<meta name="x-apple-disable-message-reformatting" content="" />
<meta content="target-densitydpi=device-dpi" name="viewport" />
<meta content="true" name="HandheldFriendly" />
<meta content="width=device-width" name="viewport" />
<meta name="format-detection" content="telephone=no, date=no, address=no, email=no, url=no" />
<style type="text/css">
table {
border-collapse: separate;
table-layout: fixed;
mso-table-lspace: 0pt;
mso-table-rspace: 0pt
}
table td {
border-collapse: collapse
}
.ExternalClass {
width: 100%
}
.ExternalClass,
.ExternalClass p,
.ExternalClass span,
.ExternalClass font,
.ExternalClass td,
.ExternalClass div {
line-height: 100%
}
body, a, li, p, h1, h2, h3 {
-ms-text-size-adjust: 100%;
-webkit-text-size-adjust: 100%;
}
html {
-webkit-text-size-adjust: none !important
}
body, #innerTable {
-webkit-font-smoothing: antialiased;
-moz-osx-font-smoothing: grayscale
}
#innerTable img+div {
display: none;
display: none !important
}
img {
Margin: 0;
padding: 0;
-ms-interpolation-mode: bicubic
}
h1, h2, h3, p, a {
line-height: inherit;
overflow-wrap: normal;
white-space: normal;
word-break: break-word
}
a {
text-decoration: none
}
h1, h2, h3, p {
min-width: 100%!important;
width: 100%!important;
max-width: 100%!important;
display: inline-block!important;
border: 0;
padding: 0;
margin: 0
}
a[x-apple-data-detectors] {
color: inherit !important;
text-decoration: none !important;
font-size: inherit !important;
font-family: inherit !important;
font-weight: inherit !important;
line-height: inherit !important
}
u + #body a {
color: inherit;
text-decoration: none;
font-size: inherit;
font-family: inherit;
font-weight: inherit;
line-height: inherit;
}
a[href^="mailto"],
a[href^="tel"],
a[href^="sms"] {
color: inherit;
text-decoration: none
}
img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:22px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}
</style>
<style type="text/css">
@media (min-width: 481px) {
.hd { display: none!important }
}
</style>
<style type="text/css">
@media (max-width: 480px) {
.hm { display: none!important }
}
</style>
<style type="text/css">
@media (min-width: 481px) {
h1,img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif}img,p{line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2,h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;font-weight:400;font-style:normal;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h2{line-height:30px;font-size:24px}h3{line-height:26px;font-size:20px}.t31{width:600px!important}.t29{width:86.66667%!important}.t27{padding-top:60px!important;padding-bottom:60px!important}.t1{width:40px!important}.t12,.t16,.t24,.t4,.t8{mso-line-height-alt:30px!important;line-height:30px!important}.t9{width:480px!important}.t3{line-height:34px!important;font-size:28px!important}.t11,.t15{line-height:28px!important;font-size:18px!important}
}
</style>
<style type="text/css">@media (min-width: 481px) {[class~="x_t31"]{width:600px!important;} [class~="x_t29"]{width:86.66667%!important;} [class~="x_t27"]{padding-top:60px!important;padding-bottom:60px!important;} [class~="x_t1"]{width:40px!important;} [class~="x_t8"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t9"]{width:480px!important;} [class~="x_t4"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t3"]{line-height:34px!important;font-size:28px!important;} [class~="x_t12"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t11"]{line-height:28px!important;font-size:18px!important;} [class~="x_t16"]{mso-line-height-alt:30px!important;line-height:30px!important;} [class~="x_t15"]{line-height:28px!important;font-size:18px!important;} [class~="x_t24"]{mso-line-height-alt:30px!important;line-height:30px!important;}}</style>
<style type="text/css" media="screen and (min-width:481px)">.moz-text-html img,.moz-text-html p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}.moz-text-html h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}.moz-text-html .t31{width:600px!important}.moz-text-html .t29{width:86.66667%!important}.moz-text-html .t27{padding-top:60px!important;padding-bottom:60px!important}.moz-text-html .t1{width:40px!important}.moz-text-html .t8{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t9{width:480px!important}.moz-text-html .t4{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t3{line-height:34px!important;font-size:28px!important}.moz-text-html .t12{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t11{line-height:28px!important;font-size:18px!important}.moz-text-html .t16{mso-line-height-alt:30px!important;line-height:30px!important}.moz-text-html .t15{line-height:28px!important;font-size:18px!important}.moz-text-html .t24{mso-line-height-alt:30px!important;line-height:30px!important}</style>
<!--[if !mso]>-->
<link href="https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;700&amp;display=swap" rel="stylesheet" type="text/css" />
<!--<![endif]-->
<!--[if mso]>
<style type="text/css">
img,p{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:400;font-style:normal;font-size:18px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px}h1{margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:34px;font-weight:700;font-style:normal;font-size:28px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px}h2{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:30px;font-weight:400;font-style:normal;font-size:24px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}h3{margin:0;Margin:0;font-family:Lato,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:26px;font-weight:400;font-style:normal;font-size:20px;text-decoration:none;text-transform:none;letter-spacing:0;direction:ltr;color:#333;text-align:left;mso-line-height-rule:exactly;mso-text-raise:2px}div.t29{width:86.66667% !important}td.t27{padding-top:60px !important;padding-bottom:60px !important}div.t4,div.t8{mso-line-height-alt:30px !important;line-height:30px !important}h1.t3{line-height:34px !important;font-size:28px !important}div.t12{mso-line-height-alt:30px !important;line-height:30px !important}p.t11{line-height:28px !important;font-size:18px !important}div.t16{mso-line-height-alt:30px !important;line-height:30px !important}p.t15{line-height:28px !important;font-size:18px !important}div.t24{mso-line-height-alt:30px !important;line-height:30px !important}
</style>
<![endif]-->
<!--[if mso]>
<xml>
<o:OfficeDocumentSettings>
<o:AllowPNG/>
<o:PixelsPerInch>96</o:PixelsPerInch>
</o:OfficeDocumentSettings>
</xml>
<![endif]-->
</head>
<body id="body" class="t35" style="min-width:100%;Margin:0px;padding:0px;background-color:#ffffff;"><div class="t34" style="background-color:#ffffff;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td class="t33" style="font-size:0;line-height:0;mso-line-height-rule:exactly;background-color:#ffffff;" valign="top" align="center">
<!--[if mso]>
<v:background xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false">
<v:fill color="#ffffff"/>
</v:background>
<![endif]-->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center" id="innerTable"><tr><td align="center">
<table class="t32" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="600" class="t31">
<![endif]-->
<!--[if !mso]>-->
<td class="t31" style="width:480px;">
<!--<![endif]-->
<div class="t30" style="display:inline-table;width:100%;text-align:center;vertical-align:top;">
<!--[if mso]>
<table role="presentation" cellpadding="0" cellspacing="0" align="center" valign="top" width="520"><tr><td width="520" valign="top"><![endif]-->
<div class="t29" style="display:inline-table;text-align:initial;vertical-align:inherit;width:100%;max-width:520px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t28" style="width:100%!important;"><tr>
<td class="t27" style="padding:50px 20px 50px 20px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%!important;"><tr><td align="center">
<table class="t2" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="40" class="t1">
<![endif]-->
<!--[if !mso]>-->
<td class="t1" style="width:80px!important;">
<!--<![endif]-->
<div style="font-size:0px;"><img class="t0" style="display:block;border:0;height:auto;width:100%;Margin:0;max-width:100%;" width="60" height="60" alt="" src="${logoUrl.value}"/></div></td>
</tr></table>
</td></tr><tr><td><div class="t4" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t6" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="440" class="t5">
<![endif]-->
<!--[if !mso]>-->
<td class="t5" style="width:440px;">
<!--<![endif]-->
<h1 class="t3" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:28px;font-weight:700;font-style:normal;font-size:22px;text-decoration:none;text-transform:none;direction:ltr;color:#000000;text-align:center;mso-line-height-rule:exactly;mso-text-raise:2px;">Welcome to ${partner.value.title}</h1></td>
</tr></table>
</td></tr><tr><td><div class="t8" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t10" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="480" class="t9">
<![endif]-->
<!--[if !mso]>-->
<td class="t9" style="width:440px;">
<!--<![endif]-->
<div style="font-size:0px;"><img class="t7" style="display:block;border:0;height:auto;width:100%;Margin:0;max-width:100%;border-radius: 0.5rem;" width="480" height="264.484375" alt="" src="${backgroundUrl.value}"/></div></td>
</tr></table>
</td></tr><tr><td><div class="t12" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t14" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="390" class="t13">
<![endif]-->
<!--[if !mso]>-->
<td class="t13" style="width:390px;">
<!--<![endif]-->
<p class="t11" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px;">Thank you for you interest in ${partner.value.title}.</p></td>
</tr></table>
</td></tr><tr><td><div class="t16" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t18" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="390" class="t17">
<![endif]-->
<!--[if !mso]>-->
<td class="t17" style="width:390px;">
<!--<![endif]-->
<p class="t15" style="margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:25px;font-weight:400;font-style:normal;font-size:16px;text-decoration:none;text-transform:none;direction:ltr;color:#121212;text-align:left;mso-line-height-rule:exactly;mso-text-raise:3px;">If you have any questions you can reach us at ${partner.value.support_email}</p></td>
</tr></table>
</td></tr><tr><td><div class="t24" style="mso-line-height-rule:exactly;mso-line-height-alt:20px;line-height:20px;font-size:1px;display:block;">&nbsp;&nbsp;</div></td></tr><tr><td align="center">
<table class="t26" role="presentation" cellpadding="0" cellspacing="0" style="Margin-left:auto;Margin-right:auto;">
<tr>
<!--[if mso]>
<td width="387" class="t25">
<![endif]-->
<!--[if !mso]>-->
<td class="t25" style="width:387px;">
<!--<![endif]-->
<div class="t23" style="display:inline-table;width:100%;text-align:left;vertical-align:top;">
<!--[if mso]>
<table role="presentation" cellpadding="0" cellspacing="0" align="left" valign="top" width="130"><tr><td width="130" valign="top"><![endif]-->
<div class="t22" style="display:inline-table;text-align:initial;vertical-align:inherit;width:33.59173%;max-width:130px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="t21" style="width:100%!important;"><tr>
<td class="t20" style="overflow:hidden;background-color:${partner.value.primary_color};text-align:center;line-height:46px;mso-line-height-rule:exactly;mso-text-raise:9px;border-radius:8px 8px 8px 8px;"><a class="t19" href="${window.location.origin}/dashboard/education" style="display:block;margin:0;Margin:0;font-family:Fira Sans,BlinkMacSystemFont,Segoe UI,Helvetica Neue,Arial,sans-serif;line-height:46px;font-weight:500;font-style:normal;font-size:16px;text-decoration:none;direction:ltr;color:#FFFFFF;text-align:center;mso-line-height-rule:exactly;mso-text-raise:9px;" target="_blank">Read more</a></td>
</tr></table>
</div>
<!--[if mso]>
</td>
</tr></table>
<![endif]-->
</div></td>
</tr></table>
</td></tr></table></td>
</tr></table>
</div>
<!--[if mso]>
</td>
</tr></table>
<![endif]-->
</div></td>
</tr></table>
</td></tr></table></td></tr></table></div></body>
</html>
`;
};
// Emails END
</script>
